import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  format, addMonths, subMonths, startOfMonth, endOfMonth,
  startOfWeek, endOfWeek, addDays, isSameMonth, isSameDay, isBefore,
} from "date-fns";
import { fr } from "date-fns/locale";
import { PhoneCall, CheckCircle2, ChevronLeft, ChevronRight, Clock, CalendarDays, Paperclip, Loader2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

const WEEK_DAYS = ["Lu", "Ma", "Me", "Je", "Ve", "Sa", "Di"];

export default function BookingPage() {
  const { token } = useParams();
  const [cfg, setCfg] = useState(null);
  const [error, setError] = useState("");
  const [month, setMonth] = useState(() => startOfMonth(new Date()));
  const [day, setDay] = useState(null);
  const [slots, setSlots] = useState(null);
  const [slot, setSlot] = useState("");
  const [form, setForm] = useState({ first_name: "", last_name: "", company: "", phone: "", email: "", description: "", website: "", instagram: "", linkedin: "" });
  const [answers, setAnswers] = useState({});
  const [duration, setDuration] = useState(0);
  const [rdvType, setRdvType] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [uploadPct, setUploadPct] = useState(null);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get(`/booking/public/${token}`)
      .then((r) => { setCfg(r.data); setDuration(r.data.duration || 60); setRdvType(r.data.types?.[0]?.id || "telephonique"); })
      .catch((e) => setError(e?.response?.status === 410
        ? "Les réservations en ligne sont temporairement désactivées."
        : "Ce lien de réservation est invalide."));
  }, [token]);

  const today = useMemo(() => { const t = new Date(); t.setHours(0, 0, 0, 0); return t; }, []);
  const maxDay = useMemo(() => addDays(today, cfg?.max_days_ahead ?? 30), [today, cfg]);

  const pickDay = async (d) => {
    setDay(d); setSlot(""); setSlots(null);
    try {
      const { data } = await api.get(`/booking/public/${token}/slots`, { params: { day: format(d, "yyyy-MM-dd"), ...(duration ? { duration } : {}) } });
      setSlots(data.slots);
    } catch { setSlots([]); }
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Fichier trop volumineux (8 Mo max)"); e.target.value = ""; return; }
    setUploadPct(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/booking/public/${token}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (p) => setUploadPct(p.total ? Math.round((p.loaded / p.total) * 100) : 50),
      });
      setAttachment(data);
      toast.success("Fichier joint");
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploadPct(null); e.target.value = ""; }
  };

  const submit = async () => {
    if (!form.first_name.trim() || !form.last_name.trim() || !form.company.trim()
        || !form.phone.trim() || !form.email.trim() || !form.description.trim()) {
      toast.error("Prénom, nom, entreprise, téléphone, email et objet de la demande sont obligatoires");
      return;
    }
    for (const q of cfg.questions || []) {
      if (q.required && !(answers[q.key] || "").trim()) {
        toast.error(`La question « ${q.label} » est obligatoire`);
        return;
      }
    }
    setSending(true);
    try {
      await api.post(`/booking/public/${token}/book`, {
        ...form, attachment, answers, duration, rdv_type: rdvType, date: format(day, "yyyy-MM-dd"), start_time: slot,
      });
      setDone({ day, slot });
    } catch (e) {
      toast.error(apiError(e));
      if (e?.response?.status === 409) pickDay(day);
    } finally { setSending(false); }
  };

  const brand = cfg?.brand_color || "#0f172a";

  if (error) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-6" data-testid="booking-error">
        <div className="bg-white border border-neutral-200 rounded-2xl shadow-sm p-10 text-center max-w-md">
          <CalendarDays className="w-8 h-8 mx-auto text-neutral-300 mb-3" />
          <p className="font-semibold text-neutral-800">{error}</p>
        </div>
      </div>
    );
  }

  if (!cfg) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" />
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-6" data-testid="booking-success">
        <div className="bg-white border border-neutral-200 rounded-2xl shadow-sm p-10 text-center max-w-md w-full">
          <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center mb-4" style={{ background: `${brand}1a` }}>
            <CheckCircle2 className="w-7 h-7" style={{ color: brand }} />
          </div>
          <h1 className="font-heading text-2xl font-bold text-neutral-900">Rendez-vous confirmé !</h1>
          <p className="text-neutral-500 mt-2 text-sm">
            Votre appel téléphonique avec <strong>{cfg.app_name}</strong> est réservé le{" "}
            <strong className="capitalize">{format(done.day, "EEEE d MMMM yyyy", { locale: fr })}</strong> à{" "}
            <strong>{done.slot}</strong>.
          </p>
          <div className="text-xs text-neutral-400 mt-4 space-y-1" data-testid="booking-success-hints">
            <p>Une confirmation vous a été envoyée par email — pensez à vérifier vos <strong className="text-neutral-500">spams</strong>.</p>
            <p>Pour modifier ou annuler votre RDV, rendez-vous dans vos emails.</p>
          </div>
        </div>
      </div>
    );
  }

  // Grille calendrier du mois affiché
  const gridStart = startOfWeek(startOfMonth(month), { weekStartsOn: 1 });
  const gridEnd = endOfWeek(endOfMonth(month), { weekStartsOn: 1 });
  const days = [];
  for (let d = gridStart; !isBefore(gridEnd, d); d = addDays(d, 1)) days.push(d);

  const inputCls = "w-full border border-neutral-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-neutral-900/10 focus:border-neutral-400 transition-colors";

  return (
    <div className="min-h-screen bg-neutral-50 py-10 px-4" data-testid="booking-page">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white border border-neutral-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="px-6 sm:px-8 py-6 border-b border-neutral-100 flex items-center gap-4">
            {cfg.app_logo_url ? (
              <img src={cfg.app_logo_url} alt={cfg.app_name} className="w-12 h-12 rounded-xl object-cover border border-neutral-200" data-testid="booking-logo" />
            ) : (
              <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white text-lg font-bold" style={{ background: brand }} data-testid="booking-logo-fallback">
                {(cfg.app_name || "S")[0]}
              </div>
            )}
            <div>
              <h1 className="font-heading text-xl sm:text-2xl font-bold text-neutral-900" data-testid="booking-title">{cfg.title || cfg.app_name}</h1>
              <p className="text-sm text-neutral-500 flex items-center gap-1.5 mt-0.5">
                <PhoneCall className="w-3.5 h-3.5" /> {cfg.description || `Rendez-vous ${(cfg.types || []).find((t) => t.id === rdvType)?.label?.toLowerCase() || "téléphonique"} · ${duration || cfg.duration} min`}
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-neutral-100">
            {/* Calendrier */}
            <div className="p-6" data-testid="booking-calendar">
              <div className="flex items-center justify-between mb-4">
                <button data-testid="booking-prev-month" disabled={isSameMonth(month, today)}
                  onClick={() => setMonth((m) => subMonths(m, 1))}
                  className="p-1.5 rounded-lg hover:bg-neutral-100 disabled:opacity-30 transition-colors">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <p className="text-sm font-semibold capitalize">{format(month, "MMMM yyyy", { locale: fr })}</p>
                <button data-testid="booking-next-month" disabled={isSameMonth(month, maxDay)}
                  onClick={() => setMonth((m) => addMonths(m, 1))}
                  className="p-1.5 rounded-lg hover:bg-neutral-100 disabled:opacity-30 transition-colors">
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
              <div className="grid grid-cols-7 gap-1 text-center">
                {WEEK_DAYS.map((d) => <span key={d} className="text-[11px] font-medium text-neutral-400 py-1">{d}</span>)}
                {days.map((d) => {
                  const disabled = !isSameMonth(d, month) || isBefore(d, today) || isBefore(maxDay, d);
                  const active = day && isSameDay(d, day);
                  return (
                    <button key={d.toISOString()} data-testid={disabled ? undefined : `booking-day-${format(d, "yyyy-MM-dd")}`}
                      disabled={disabled} onClick={() => pickDay(d)}
                      className={`aspect-square rounded-lg text-sm transition-colors ${
                        active ? "text-white font-semibold" : disabled ? "text-neutral-300 cursor-not-allowed" : "hover:bg-neutral-100 text-neutral-700"
                      }`}
                      style={active ? { background: brand } : undefined}>
                      {format(d, "d")}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Créneaux + formulaire */}
            <div className="p-6" data-testid="booking-slots-panel">
              {!day && (
                <div className="h-full flex flex-col items-center justify-center text-center py-10">
                  <Clock className="w-7 h-7 text-neutral-300 mb-3" />
                  <p className="text-sm text-neutral-500">Sélectionnez un jour pour voir les créneaux disponibles.</p>
                </div>
              )}
              {day && slots === null && (
                <div className="py-10 flex justify-center"><div className="w-5 h-5 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" /></div>
              )}
              {day && slots !== null && !slot && (
                <>
                  <p className="text-sm font-semibold capitalize mb-3">{format(day, "EEEE d MMMM", { locale: fr })}</p>
                  {slots.length === 0 ? (
                    <p className="text-sm text-neutral-400 py-6" data-testid="booking-no-slots">Aucun créneau disponible ce jour-là. Essayez un autre jour.</p>
                  ) : (
                    <div className="grid grid-cols-3 gap-2 max-h-64 overflow-y-auto pr-1" data-testid="booking-slots">
                      {slots.map((s) => (
                        <button key={s} data-testid={`booking-slot-${s.replace(":", "h")}`} onClick={() => setSlot(s)}
                          className="border border-neutral-200 rounded-lg py-2 text-sm font-medium hover:text-white transition-colors"
                          onMouseEnter={(e) => { e.currentTarget.style.background = brand; e.currentTarget.style.borderColor = brand; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = ""; e.currentTarget.style.borderColor = ""; }}>
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
              {day && slot && (
                <div className="space-y-3" data-testid="booking-form">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold capitalize">{format(day, "EEEE d MMMM", { locale: fr })} · {slot}</p>
                    <button data-testid="booking-change-slot" onClick={() => setSlot("")} className="text-xs text-neutral-500 hover:text-neutral-900 underline underline-offset-2">Changer</button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <input data-testid="booking-firstname" placeholder="Votre prénom *" className={inputCls} value={form.first_name}
                      onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))} />
                    <input data-testid="booking-lastname" placeholder="Votre nom *" className={inputCls} value={form.last_name}
                      onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))} />
                  </div>
                  <input data-testid="booking-company" placeholder="Nom de votre entreprise *" className={inputCls} value={form.company}
                    onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))} />
                  <input data-testid="booking-phone" placeholder="Votre téléphone *" className={inputCls} value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
                  <input data-testid="booking-email" type="email" placeholder="Votre email *" className={inputCls} value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
                  {(cfg.types || []).length > 1 && (
                    <select data-testid="booking-type" className={inputCls} value={rdvType}
                      onChange={(e) => setRdvType(e.target.value)}>
                      {cfg.types.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                    </select>
                  )}
                  {(cfg.durations || []).length > 1 && (
                    <select data-testid="booking-duration" className={inputCls} value={duration}
                      onChange={(e) => { setDuration(Number(e.target.value)); setSlot(""); if (day) pickDay(day); }}>
                      {cfg.durations.map((d) => <option key={d} value={d}>{d >= 60 ? `${d / 60} h` : `${d} min`}</option>)}
                    </select>
                  )}
                  {(cfg.questions || []).map((q) => (
                    <input key={q.key} data-testid={`booking-question-${q.key}`} className={inputCls}
                      placeholder={`${q.label}${q.required ? " *" : ""}`} value={answers[q.key] || ""}
                      onChange={(e) => setAnswers((a) => ({ ...a, [q.key]: e.target.value }))} />
                  ))}
                  <div>
                    <textarea data-testid="booking-description" placeholder="L'objet de votre demande *" rows={3}
                      className={`${inputCls} resize-none`} value={form.description}
                      onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
                    <p className="text-[11px] text-neutral-400 mt-1">Soyez le plus précis possible dans votre demande (contexte, besoins, objectifs…).</p>
                  </div>

                  <div className="border-t border-neutral-100 pt-3 space-y-3" data-testid="booking-extra">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-neutral-400">Site web et réseaux sociaux (facultatif)</p>
                    <input data-testid="booking-website" type="url" placeholder="Site web (https://…)" className={inputCls} value={form.website}
                      onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))} />
                    <div className="grid grid-cols-2 gap-2">
                      <input data-testid="booking-instagram" placeholder="Instagram (@ ou lien)" className={inputCls} value={form.instagram}
                        onChange={(e) => setForm((f) => ({ ...f, instagram: e.target.value }))} />
                      <input data-testid="booking-linkedin" placeholder="LinkedIn (lien)" className={inputCls} value={form.linkedin}
                        onChange={(e) => setForm((f) => ({ ...f, linkedin: e.target.value }))} />
                    </div>
                    <div>
                      <label data-testid="booking-attachment-btn"
                        className={`flex items-center justify-center gap-2 border border-dashed rounded-lg py-2.5 text-xs font-medium cursor-pointer transition-colors ${attachment ? "border-green-300 bg-green-50 text-green-700" : "border-neutral-300 text-neutral-500 hover:border-neutral-400 hover:text-neutral-700"}`}>
                        {uploadPct !== null ? (
                          <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Envoi… {uploadPct} %</>
                        ) : attachment ? (
                          <><CheckCircle2 className="w-3.5 h-3.5" /> {attachment.name}</>
                        ) : (
                          <><Paperclip className="w-3.5 h-3.5" /> Joindre un fichier (image ou PDF, 8 Mo max)</>
                        )}
                        <input type="file" accept="image/*,.pdf" className="hidden" data-testid="booking-attachment-input" onChange={uploadFile} disabled={uploadPct !== null} />
                      </label>
                      {attachment && (
                        <button data-testid="booking-attachment-remove" onClick={() => setAttachment(null)}
                          className="text-[11px] text-neutral-400 hover:text-red-500 mt-1 transition-colors">Retirer le fichier</button>
                      )}
                    </div>
                  </div>

                  <button data-testid="booking-submit" onClick={submit} disabled={sending || uploadPct !== null}
                    className="w-full text-white rounded-lg py-2.5 text-sm font-semibold transition-opacity disabled:opacity-50"
                    style={{ background: brand }}>
                    {sending ? "Réservation…" : "Confirmer le rendez-vous"}
                  </button>
                  <p className="text-[11px] text-neutral-400 text-center">Vous recevrez une confirmation par email.</p>
                </div>
              )}
            </div>
          </div>
        </div>
        <p className="text-center text-xs text-neutral-400 mt-4">Propulsé par DGA</p>
      </div>
    </div>
  );
}
